import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useNumberRangeFilter from '../../../../components/Crud/filters/hooks/numberRangeFilter/useNumberRangeFilter';
import { NumberRangeFilterTypes } from '../../../../components/FilterV3/components/NumberRangeFilter/numberRangeFilter.types';
import useDataQuery from '../../../../hooks/useDataQuery/useDataQuery';
import useHandleAppException, {
  AppExceptionResponse,
} from '../../../../hooks/useHandleAppException';
import LangEnum from '../../../../lang/LangEnum';
import useOrderBookStore from '../../../../stores/OrderBook/orderBook.store';
import useIsAdminSeller from '../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useTranslation } from '../../../translation/TranslationContext';
import { onlineCatalogsRepository } from '../../repositories/onlineCatalogs.repository';
import { CATALOG_FILTERS_KEYS } from '../catalogFilters.constants';

export interface UseSellInPriceRangeFilterParams {
  catalogId: string;
  shouldIgnoreDefaultFilters: boolean;
  isEnabled: boolean;
}

export const useSellInPriceRangeFilter = ({
  catalogId,
  shouldIgnoreDefaultFilters,
  isEnabled,
}: UseSellInPriceRangeFilterParams) => {
  const { translate } = useTranslation();
  const isAdminSeller = useIsAdminSeller();
  const { handleErrorResponse } = useHandleAppException();
  const [selectedOrder] = useOrderBookStore(useShallow((store) => [store.selectedOrder]));

  const orderId = selectedOrder?.id;
  const priceTableId = selectedOrder?.priceTable?.id;

  const lastHandledErrorRef = useRef<unknown>(null);

  const {
    data: rangeData,
    isError,
    error,
  } = useDataQuery(
    {
      queryKey: ['catalog-sell-in-price-range', catalogId, orderId, priceTableId, isAdminSeller],
      queryFn: async () => {
        const repository = onlineCatalogsRepository(catalogId);
        const response = await repository.getSellInPriceRange({
          orderId,
          priceTableId,
          isAdminSeller,
        });
        if (!response.ok || !response.data) {
          throw response;
        }
        return response.data;
      },
    },
    { enabled: isEnabled && !!catalogId },
  );

  useEffect(() => {
    if (!isError || !error || lastHandledErrorRef.current === error) {
      return;
    }
    lastHandledErrorRef.current = error;

    const appError = error as AppExceptionResponse;
    if (appError?.data) {
      const message = handleErrorResponse(appError);
      snackbarStore.handleMessage({ type: 'error', message });
    } else {
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.GLOBAL_ERROR_MESSAGE),
      });
    }
  }, [isError, error, handleErrorResponse, translate]);

  const minAvailableValue = rangeData?.min ?? 0;
  const maxAvailableValue = rangeData?.max ?? 0;

  const filter = useNumberRangeFilter({
    type: NumberRangeFilterTypes.MONETARY,
    minValueKey: CATALOG_FILTERS_KEYS.sellInPriceMin,
    maxValueKey: CATALOG_FILTERS_KEYS.sellInPriceMax,
    minAvailableValue,
    maxAvailableValue,
    shouldIgnoreDefaultFilters,
  });

  return {
    ...filter,
    minAvailableValue,
    maxAvailableValue,
  };
};

export default useSellInPriceRangeFilter;
